import React, { useState } from "react";
import { PlayCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function youtubeId(url) {
  if (!url) return "";
  const m = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : "";
}

/**
 * "Factory in Action" video gallery with a lightbox player.
 * Shared by the client Product Detail page and the public /specs spec sheet.
 * `videos` is an array of { title, url } (YouTube). Renders nothing if empty.
 */
export default function FactoryVideoGallery({ videos = [] }) {
  const [playing, setPlaying] = useState(null);
  const valid = (videos || []).filter((v) => youtubeId(v.url));
  if (valid.length === 0) return null;

  return (
    <>
      <section className="mt-16 md:mt-20" data-testid="product-factory-video">
        <div className="flex items-center gap-2 text-[#FF5A1F] mt-tiny">
          <PlayCircle size={18} weight="fill" /> Factory in Action
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-zinc-900 mt-2">
          Behind the Scenes at Ston Link Manufacturing
        </h2>
        <p className="mt-2 text-sm md:text-base text-zinc-600 max-w-2xl">
          See how this material is produced, finished and quality-checked before it reaches your project.
        </p>

        <div
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
          data-testid="factory-video-grid"
        >
          {valid.map((v) => {
            const id = youtubeId(v.url);
            return (
              <button
                key={v.url}
                onClick={() => setPlaying(v)}
                data-testid={`factory-video-thumb-${id}`}
                className="group text-left"
              >
                <div
                  className="relative aspect-video overflow-hidden bg-black border border-zinc-200 group-hover:border-zinc-900 transition-colors"
                  style={{ borderRadius: "10px" }}
                >
                  <img
                    src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
                    alt={v.title || "Factory video"}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="w-14 h-14 rounded-full bg-white/90 grid place-items-center shadow-lg group-hover:scale-110 transition-transform">
                      <PlayCircle size={30} weight="fill" className="text-zinc-900" />
                    </span>
                  </div>
                </div>
                {v.title && (
                  <div className="mt-2.5 font-medium text-zinc-900 text-sm md:text-base group-hover:text-[#0047AB] transition-colors">
                    {v.title}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent
          className="max-w-4xl p-0 overflow-hidden bg-black border-zinc-800"
          data-testid="factory-video-lightbox"
        >
          <DialogHeader className="px-5 pt-4 pb-3">
            <DialogTitle className="text-white text-left text-base md:text-lg">
              {playing?.title || "Factory in Action"}
            </DialogTitle>
          </DialogHeader>
          {playing && (
            <div className="relative w-full aspect-video">
              <iframe
                title={playing.title || "Factory video"}
                src={`https://www.youtube-nocookie.com/embed/${youtubeId(playing.url)}?autoplay=1&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
